import React, { useCallback, useState } from 'react';
import moment from 'moment';

import { IBuilderSaveResult } from 'components/EmbeddedBuilder';
import { useTypedSelector } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { DEFAULT_TIMEZONE } from '../configStudio/configModel';
import { ConfigHandoff, ConfigIntent, ConfigSubmit } from '../configStudio/configSubmission';
import { ConfigReviewPanel, TC } from '../configStudio/kit/ConfigPanel';
import ConfigResultView from '../configStudio/kit/ConfigResultView';
import ConfigWizardRoot from '../configStudio/kit/ConfigWizardRoot';
import { RewardExpiryStep, RewardLimitsStep, RewardVisibilityStep } from './RewardDetailSteps';
import { RewardSourceStep, RewardTypeStep } from './RewardSourceSteps';
import { buildRewardValues, RewardWizardValues } from './rewardAdapter';
import {
  initialRewardState,
  IRewardStepProps,
  nextRewardStep,
  previousRewardStep,
  REWARD_STEP_NUMBER,
  RewardStep,
  rewardStepAt,
  RewardWizardState
} from './rewardModel';
import { rewardReviewRows } from './rewardReviewRows';

const RV = `${TC}.reward.review`;

const STEPS: Record<Exclude<RewardStep, 'review'>, React.FC<IRewardStepProps>> = {
  type: RewardTypeStep,
  source: RewardSourceStep,
  visibility: RewardVisibilityStep,
  expiry: RewardExpiryStep,
  limits: RewardLimitsStep
};

interface IRewardWizardProps {
  onSubmit: ConfigSubmit<RewardWizardValues>;
  onExit: () => void;
  /** Told about every record a save created. */
  onHandoff: ConfigHandoff;
  initialState?: RewardWizardState;
}

// 54 / 62 draw the start already filled with today; the user may move it.
const startingNow = (state: RewardWizardState): RewardWizardState => ({
  ...state,
  beginsAt: moment().startOf('minute').format()
});

interface ISaved {
  name: string;
  result: IBuilderSaveResult;
}

/** The standalone "Let's Setup your Reward" flow (56 → 57-61 / 69 / 70 → 62 → 63 → 64 → 66). */
const RewardWizard: React.FC<IRewardWizardProps> = ({
  onSubmit,
  onExit,
  onHandoff,
  initialState
}) => {
  const { t } = useMultiLanguageTranslation();
  const timezone = useTypedSelector(
    store => store.global?.tenantConfig?.localization?.timezone ?? DEFAULT_TIMEZONE
  );
  const [state, setState] = useState<RewardWizardState>(
    () => initialState ?? startingNow(initialRewardState())
  );
  const [saved, setSaved] = useState<ISaved | null>(null);
  const update = useCallback(
    (change: (current: RewardWizardState) => RewardWizardState) => setState(change),
    []
  );
  const next = useCallback(
    () => setState(current => ({ ...current, step: nextRewardStep(current.step) })),
    []
  );
  const back = () =>
    state.step === 'type'
      ? onExit()
      : setState(current => ({ ...current, step: previousRewardStep(current.step) }));
  const jump = useCallback(
    (number: number) => setState(current => ({ ...current, step: rewardStepAt(number) })),
    []
  );

  const submit = async (intent: ConfigIntent) => {
    const now = moment().toISOString();
    const result = await onSubmit({
      intent,
      values: buildRewardValues(state, { t, timezone, now: () => now })
    });
    if (result !== null) {
      setSaved({ name: state.name.trim(), result });
      setState(startingNow(initialRewardState()));
      onHandoff(result);
    }
  };

  const renderStep = () => {
    if (saved !== null) {
      return (
        <ConfigResultView
          entity={t(`${TC}.reward.entity`)}
          name={saved.name}
          result={saved.result}
          onCreateAnother={() => setSaved(null)}
        />
      );
    }
    if (state.step === 'review') {
      return (
        <ConfigReviewPanel<RewardStep>
          title={t(`${RV}.ready`)}
          question={t(`${RV}.question`)}
          onBack={back}
          nameLabel={t(`${RV}.name`)}
          namePlaceholder={t(`${RV}.namePlaceholder`)}
          nameRequired={t(`${RV}.nameRequired`)}
          name={state.name}
          onName={name => update(current => ({ ...current, name }))}
          rows={rewardReviewRows(state, t, timezone, moment().format())}
          onEdit={step => update(current => ({ ...current, step }))}
          confirmLabel={t(`${TC}.confirm`)}
          onSubmit={submit}
          testId="orbit-reward-step-review"
        />
      );
    }
    const Step = STEPS[state.step];
    return <Step state={state} update={update} next={next} back={back} />;
  };

  return (
    <ConfigWizardRoot
      testId="orbit-reward-wizard"
      step={state.step}
      onJump={saved === null && REWARD_STEP_NUMBER[state.step] !== null ? jump : null}
    >
      {renderStep()}
    </ConfigWizardRoot>
  );
};

export default RewardWizard;
